/**
 * NotificationPromptModal - Prompts user to enable push notifications
 * 
 * Shows after login on native platforms (Android/iOS) following common UX patterns.
 * Does NOT show on web since web push uses different APIs.
 * 
 * Features:
 * - Shows contextual message explaining benefits
 * - Remembers if user dismissed (won't spam)
 * - Only shows once per device unless user clears storage
 * - Uses i18n for localization (modals namespace)
 * 
 * @author Heseda AI Team
 * @version 1.1
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './NotificationPromptModal.css';

interface NotificationPromptModalProps {
  isOpen: boolean;
  onEnable: () => void;
  onDismiss: () => void;
}

/**
 * LocalStorage key for tracking prompt dismissal
 * Prevents showing the prompt repeatedly after user says "Not Now"
 */
export const NOTIFICATION_PROMPT_DISMISSED_KEY = 'heseda_notification_prompt_dismissed';

/**
 * Check if user has previously dismissed the notification prompt
 */
export function hasUserDismissedNotificationPrompt(): boolean {
  try {
    return localStorage.getItem(NOTIFICATION_PROMPT_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Mark that user has dismissed the notification prompt
 */
export function markNotificationPromptDismissed(): void {
  try {
    localStorage.setItem(NOTIFICATION_PROMPT_DISMISSED_KEY, 'true');
  } catch {
    // Ignore storage errors
  }
}

export default function NotificationPromptModal({
  isOpen,
  onEnable,
  onDismiss,
}: NotificationPromptModalProps) {
  const { t } = useTranslation('modals');

  if (!isOpen) return null;

  return createPortal(
    <div className="notification-prompt-overlay" role="dialog" aria-modal="true">
      <div className="notification-prompt-modal">
        {/* Bell icon */}
        <div className="notification-prompt-icon">
          <svg 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>

        <h2 className="notification-prompt-title">
          {t('notificationPrompt.title')}
        </h2>
        <p className="notification-prompt-description">
          {t('notificationPrompt.description')}
        </p>

        <ul className="notification-prompt-benefits">
          <li>
            <span className="benefit-check">✓</span>
            {t('notificationPrompt.benefits.recommendations')}
          </li>
          <li>
            <span className="benefit-check">✓</span>
            {t('notificationPrompt.benefits.orders')}
          </li>
          <li>
            <span className="benefit-check">✓</span>
            {t('notificationPrompt.benefits.offers')}
          </li>
        </ul>

        <div className="notification-prompt-actions">
          <button 
            className="notification-prompt-enable"
            onClick={onEnable}
          >
            {t('notificationPrompt.enable')}
          </button>
          <button 
            className="notification-prompt-dismiss"
            onClick={onDismiss}
          >
            {t('notificationPrompt.dismiss')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
